export interface ShaderParameter {
  name: string;
  type: 'float' | 'vec2' | 'vec3' | 'vec4' | 'int' | 'bool';
  defaultValue: number | number[];
  min?: number;
  max?: number;
  description?: string;
  category?: 'time' | 'color' | 'geometry' | 'effects' | 'audio';
}

export interface AudioMapping {
  parameterId: string;
  audioSource: 'bass' | 'mid' | 'treble' | 'volume';
  intensity: number; // 0.0 to 1.0
  offset: number;    // base value
}

export class ShaderParameterExtractor {
  // Common parameter patterns found in Shadertoy shaders
  private static readonly PARAMETER_PATTERNS = [
    // #define patterns - only match clear numeric values
    { pattern: /#define\s+(\w+)\s+([0-9.-]+)(?:\s|$)/g, type: 'float' as const },
    
    // const declarations - only match clear numeric declarations
    { pattern: /const\s+float\s+(\w+)\s*=\s*([0-9.-]+)/g, type: 'float' as const },
    { pattern: /const\s+vec2\s+(\w+)\s*=\s*vec2\s*\(\s*([0-9.-]+)\s*,\s*([0-9.-]+)\s*\)/g, type: 'vec2' as const },
    { pattern: /const\s+vec3\s+(\w+)\s*=\s*vec3\s*\(\s*([0-9.-]+)\s*,\s*([0-9.-]+)\s*,\s*([0-9.-]+)\s*\)/g, type: 'vec3' as const },
    { pattern: /const\s+int\s+(\w+)\s*=\s*([0-9]+)/g, type: 'int' as const },
  ];

  // Audio-reactive parameter suggestions
  private static readonly AUDIO_MAPPINGS = {
    bass: ['amplitude', 'scale', 'size', 'thickness', 'radius', 'intensity'],
    mid: ['speed', 'frequency', 'rotation', 'movement', 'wobble'],
    treble: ['brightness', 'sparkle', 'detail', 'noise', 'shimmer'],
    volume: ['overall', 'global', 'master', 'total', 'main']
  };

  static extractParameters(shaderCode: string): ShaderParameter[] {
    const parameters: ShaderParameter[] = [];
    const foundNames = new Set<string>();

    // First, find all function names to avoid conflicts
    const functionNames = this.findFunctionNames(shaderCode);
    console.log('Found function names in shader:', Array.from(functionNames));
    
    // Find preprocessor directives to exclude from parameters
    const preprocessorVars = this.findPreprocessorVariables(shaderCode);
    console.log('Found preprocessor variables:', Array.from(preprocessorVars));

    // Extract parameters using patterns
    for (const patternObj of this.PARAMETER_PATTERNS) {
      const { pattern, type } = patternObj;
      const category = ('category' in patternObj ? patternObj.category : undefined) as ShaderParameter['category'] | undefined;
      
      let match;
      const regex = new RegExp(pattern.source, pattern.flags);
      
      while ((match = regex.exec(shaderCode)) !== null) {
        const name = match[1];
        
        // Skip if already found, if it's a built-in/common variable, if it's a function name, or if it's a preprocessor variable
        if (foundNames.has(name) || this.isBuiltInVariable(name) || functionNames.has(name) || preprocessorVars.has(name)) {
          continue;
        }
        
        foundNames.add(name);
        
        const param: ShaderParameter = {
          name,
          type,
          defaultValue: this.parseDefaultValue(match, type),
          category: category || this.categorizeParameter(name),
          description: this.generateDescription(name),
          ...this.getParameterRange(name, type)
        };
        
        parameters.push(param);
        console.log('Extracted parameter:', param);
      }
    }

    console.log('Total parameters extracted:', parameters.length);
    return parameters.sort((a, b) => {
      // Sort by category, then by name
      const categoryOrder = ['audio', 'time', 'geometry', 'color', 'effects'];
      const aCat = categoryOrder.indexOf(a.category || 'effects');
      const bCat = categoryOrder.indexOf(b.category || 'effects');
      
      if (aCat !== bCat) return aCat - bCat;
      return a.name.localeCompare(b.name);
    });
  }

  private static findFunctionNames(shaderCode: string): Set<string> {
    const functionNames = new Set<string>();
    
    // Find function declarations
    const funcRegex = /(?:vec[234]|float|int|bool|void)\s+(\w+)\s*\(/g;
    let match;
    while ((match = funcRegex.exec(shaderCode)) !== null) {
      functionNames.add(match[1]);
    }
    
    // Also find function calls to be extra safe
    const callRegex = /(\w+)\s*\(/g;
    while ((match = callRegex.exec(shaderCode)) !== null) {
      functionNames.add(match[1]);
    }
    
    return functionNames;
  }

  private static findPreprocessorVariables(shaderCode: string): Set<string> {
    const preprocessorVars = new Set<string>();
    
    // Find variables used in #if, #ifdef, #ifndef directives
    const ifRegex = /#if\w*\s+([A-Z_][A-Z0-9_]*)/g;
    let match;
    while ((match = ifRegex.exec(shaderCode)) !== null) {
      preprocessorVars.add(match[1]);
    }
    
    // Find variables used in #if expressions (like #if VAR == 1)
    const ifExprRegex = /#if\s+([A-Z_][A-Z0-9_]*)\s*[=!<>]/g;
    while ((match = ifExprRegex.exec(shaderCode)) !== null) {
      preprocessorVars.add(match[1]);
    }
    
    return preprocessorVars;
  }

  static generateAudioMappings(parameters: ShaderParameter[]): AudioMapping[] {
    const mappings: AudioMapping[] = [];

    for (const param of parameters) {
      const audioSource = this.suggestAudioSource(param.name);
      if (audioSource) {
        mappings.push({
          parameterId: param.name,
          audioSource,
          intensity: this.suggestIntensity(param.name, audioSource),
          offset: Array.isArray(param.defaultValue) ? param.defaultValue[0] : param.defaultValue
        });
      }
    }

    return mappings;
  }

  private static parseDefaultValue(match: RegExpMatchArray, type: string): number | number[] {
    switch (type) {
      case 'float':
      case 'int':
        return parseFloat(match[2]) || 0;
      case 'vec2':
        return [parseFloat(match[2]) || 0, parseFloat(match[3]) || 0];
      case 'vec3':
        return [parseFloat(match[2]) || 0, parseFloat(match[3]) || 0, parseFloat(match[4]) || 0];
      case 'vec4':
        return [parseFloat(match[2]) || 0, parseFloat(match[3]) || 0, parseFloat(match[4]) || 0, parseFloat(match[5]) || 1];
      default:
        return 0;
    }
  }

  private static isBuiltInVariable(name: string): boolean {
    const builtIns = [
      'iTime', 'iResolution', 'iMouse', 'iDate', 'iFrame', 'iTimeDelta',
      'iAudioBass', 'iAudioMid', 'iAudioTreble', 'iAudioVolume',
      'PI', 'E', 'gl_FragCoord', 'fragColor', 'main', 'mainImage',
      'sin', 'cos', 'tan', 'length', 'normalize', 'dot', 'cross', 'mix',
      'step', 'smoothstep', 'clamp', 'min', 'max', 'abs', 'floor', 'ceil',
      'round', 'sqrt', 'pow', 'exp', 'log', 'atan', 'asin', 'acos',
      'radians', 'degrees', 'texture', 'vec2', 'vec3', 'vec4', 'mat2', 'mat3', 'mat4',
      // Common Shadertoy function names
      'map', 'color', 'skyColor', 'sunDirection', 'atmosphere', 'noise',
      'fbm', 'hash', 'rotate', 'sdf', 'march', 'raymarch'
    ];
    return builtIns.includes(name) || name.length <= 2;
  }

  private static categorizeParameter(name: string): ShaderParameter['category'] {
    const nameLower = name.toLowerCase();
    
    if (/time|speed|rate|freq/i.test(nameLower)) return 'time';
    if (/color|hue|sat|bright|contrast/i.test(nameLower)) return 'color';
    if (/scale|size|radius|width|height|thick/i.test(nameLower)) return 'geometry';
    if (/amp|volume|intensity|strength/i.test(nameLower)) return 'audio';
    
    return 'effects';
  }

  private static generateDescription(name: string): string {
    const nameLower = name.toLowerCase();
    
    if (/amp/i.test(nameLower)) return 'Controls the amplitude/strength of the effect';
    if (/speed/i.test(nameLower)) return 'Controls the animation speed';
    if (/scale/i.test(nameLower)) return 'Controls the size/scale of elements';
    if (/color/i.test(nameLower)) return 'Controls color properties';
    if (/bright/i.test(nameLower)) return 'Controls brightness levels';
    if (/freq/i.test(nameLower)) return 'Controls frequency/repetition';
    if (/thick/i.test(nameLower)) return 'Controls thickness of lines/shapes';
    
    return `Controls the ${name} parameter`;
  }

  private static getParameterRange(name: string, type: string): { min?: number; max?: number } {
    const nameLower = name.toLowerCase();
    
    // Specific parameter name handling for better ranges
    if (nameLower === 'cutoff') {
      return { min: 0.0, max: 1.0 };
    }
    
    if (nameLower === 'start') {
      return { min: 0, max: 50 };
    }
    
    if (nameLower === 'planedist') {
      return { min: 0.1, max: 5.0 };
    }
    
    if (nameLower === 'furthest' || nameLower === 'limit') {
      return { min: 1, max: 20 };
    }
    
    // General patterns
    if (/amp|intensity|strength/i.test(nameLower)) {
      return { min: 0, max: 20 };
    }
    
    if (/speed|rate/i.test(nameLower)) {
      return { min: 0, max: 10 };
    }
    
    if (/scale|size/i.test(nameLower)) {
      return { min: 0.1, max: 5 };
    }
    
    if (/bright|contrast/i.test(nameLower)) {
      return { min: 0, max: 2 };
    }
    
    if (/freq/i.test(nameLower)) {
      return { min: 1, max: 50 };
    }
    
    if (/dist|distance/i.test(nameLower)) {
      return { min: 0.1, max: 10 };
    }
    
    // Default ranges based on type
    if (type === 'float') {
      return { min: 0, max: 20 };
    }
    
    if (type === 'int') {
      return { min: 1, max: 50 };
    }
    
    return {};
  }

  private static suggestAudioSource(paramName: string): AudioMapping['audioSource'] | null {
    const nameLower = paramName.toLowerCase();
    
    // Check each audio source for keyword matches
    for (const [audioSource, keywords] of Object.entries(this.AUDIO_MAPPINGS)) {
      if (keywords.some(keyword => nameLower.includes(keyword))) {
        return audioSource as AudioMapping['audioSource'];
      }
    }
    
    // Additional heuristics for common parameter names
    if (/cutoff|cut/i.test(nameLower)) return 'treble';
    if (/start|time|speed/i.test(nameLower)) return 'mid';
    if (/dist|distance|plane/i.test(nameLower)) return 'bass';
    if (/furthest|far|limit/i.test(nameLower)) return 'volume';
    
    // Default suggestion for any numeric parameter
    return 'bass';
  }

  private static suggestIntensity(paramName: string, audioSource: string): number {
    const nameLower = paramName.toLowerCase();
    
    // Suggest different intensities based on parameter type and audio source
    if (/amp|intensity/i.test(nameLower)) {
      return audioSource === 'bass' ? 0.8 : 0.6;
    }
    
    if (/speed|rate/i.test(nameLower)) {
      return 0.4;
    }
    
    if (/scale|size/i.test(nameLower)) {
      return audioSource === 'bass' ? 0.7 : 0.3;
    }
    
    if (/bright|color/i.test(nameLower)) {
      return 0.5;
    }
    
    return 0.5; // Default intensity
  }
}